import * as React from 'react'
import { graphql, useStaticQuery, withPrefix } from 'gatsby'
import styled from 'styled-components'
import { Helmet } from 'react-helmet'
import Layout from '../components/Layout'
import {
  constants,
  TextSettingsByBreakpoint,
  TextSettingsProps,
} from '../constants'
import KeyVisual from '../components/common/KeyVisual'
import MainTitle from '../components/common/MainTitle'
import Header from '../components/Header'
import SPKeyText from '../components/common/SPKeyText'
import Section from '../components/common/Section'
import SubSectionBlock from '../components/common/SubSectionBlock'
import LinkWithArrow from '../components/common/LinkWithArrow'
import SubFooter from '../components/SubFooter'
import TitleImage from '../components/common/TitleImage'
import PackagingDevelopment from '../components/PackagingDevelopment'
import Favicon from '../assets/favicon.ico'
import { Language } from '../Language'
import { FontsContext } from '../context'

const query = graphql`
  query {
    kv: file(relativePath: { eq: "top_kv.png" }) {
      childImageSharp {
        fluid(maxWidth: 1600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    logo: file(relativePath: { eq: "shiseido_white.png" }) {
      childImageSharp {
        fluid(maxWidth: 185) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    formulaDevelopment: file(relativePath: { eq: "top/01.png" }) {
      childImageSharp {
        fluid(maxWidth: 400, srcSetBreakpoints: [400]) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    packagingDevelopment: file(relativePath: { eq: "top/02.png" }) {
      childImageSharp {
        fluid(maxWidth: 400, srcSetBreakpoints: [400]) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`

interface Props {
  messages: any
  titles: {
    KVTitle: any
    OurPolicies?: any
    RelatedWebsites?: any
  }
  language: Language
}

const HomeIndex = ({ messages, titles, language }: Props) => {
  const data = useStaticQuery(query)
  const fonts = React.useContext(FontsContext)

  return (
    <>
      <Helmet>
        <meta charSet="utf-8"></meta>
        <title>{messages.top.meta.title}</title>
        <meta name="description" content={messages.top.meta.description}></meta>
        <meta name="keywords" content={messages.top.meta.keywords}></meta>
        <meta
          property="og:image"
          content="http://www.shiseidogroup.com/cmn_new/img/fb_icon_img.jpg"
        ></meta>
        <link
          rel="shortcut icon"
          href={Favicon}
          type="image/vnd.microsoft.ico"
        ></link>
      </Helmet>
      <Layout
        messages={messages}
        language={language}
        keyVisual={() => (
          <KeyVisual
            fluidImage={data.kv.childImageSharp.fluid}
            header={() => (
              <Header
                fluidImage={data.logo.childImageSharp.fluid}
                language={language}
                messages={messages}
              />
            )}
            contents={() => (
              <>
                <Title>
                  <TitleImage
                    spWidth="300px"
                    pcWidth="794px"
                    src={titles.KVTitle}
                    alt={messages.top.keyVisual.title}
                  />
                </Title>
                <Text settings={fonts.texts.topKvText}>
                  {messages.top.keyVisual.text}
                </Text>
                <Footnote settings={fonts.texts.topKvFootnote}>
                  {messages.top.keyVisual.footnote}
                </Footnote>
              </>
            )}
          />
        )}
        subFooter={() => (
          <SubFooter messages={messages} titleImg={titles.RelatedWebsites} />
        )}
      >
        <SPKeyText>
          <p>{messages.top.keyVisual.text}</p>
          <p>{messages.top.keyVisual.footnote}</p>
        </SPKeyText>
        <Section>
          <MainTitle settings={fonts.texts.title1}>
            {titles.OurPolicies ? (
              <TitleImage
                spWidth="129px"
                pcWidth="184px"
                src={titles.OurPolicies}
                alt={messages.top.title}
              />
            ) : (
              messages.top.title
            )}
          </MainTitle>
          <SubSectionBlock
            title={messages.top.corporateProduct.title}
            body={messages.top.corporateProduct.text}
          />
          <SubSectionBlock
            title={messages.top.formulaDevelopment.title}
            body={messages.top.formulaDevelopment.text}
            sideImage={data.formulaDevelopment.childImageSharp.fluid}
            imageAlt={messages.top.formulaDevelopment.title}
            link={() => (
              <LinkWithArrow
                fonts={fonts}
                settings={fonts.texts.arrowLink}
                href={withPrefix(`/${language}/ingredients/`)}
              >
                {messages.top.linkToIngredients}
              </LinkWithArrow>
            )}
          />
          <PackagingDevelopment
            title={messages.top.packagingDevelopment.title}
            text={messages.top.packagingDevelopment.text}
            subtitle={messages.top.packagingDevelopment.subtitle}
            blocks={messages.top.packagingDevelopment.blocks}
            footnote={messages.top.packagingDevelopment.footnote}
            sideImage={data.packagingDevelopment.childImageSharp.fluid}
            imageAlt={messages.top.packagingDevelopment.title}
          />
        </Section>
      </Layout>
    </>
  )
}

const Title = styled.div`
  font-size: ${constants.font.size.sp.headings.extraLarge};
  color: ${constants.colors.text.white};
  text-align: center;

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    font-size: 90px;
    margin-bottom: 80px;
  }

  > img {
    width: 300px;

    @media only screen and (min-width: ${constants.breakpoints.pc}) {
      width: 794px;
    }
  }
`

const Text = styled.p<TextSettingsProps>`
  display: none;

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    display: block;
    margin: 0;
    margin-bottom: 45px;
    font-size: ${({ settings }) => settings.pc.size};
    color: ${constants.colors.text.white};
    line-height: 1.4;
  }
`

const Footnote = styled.p<TextSettingsProps>`
  display: none;

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    display: block;
    margin: 0;
    font-size: ${({ settings }) => settings.pc.size};
    color: ${constants.colors.text.white};
    line-height: 1.4;
    letter-spacing: -0.36px;
  }
`

export default HomeIndex
